import axios from '@/axios/axios'
import debounce from 'debounce-promise'

const CMSUrl = process.env.VUE_APP_CMS_URL

export default {
  namespaced: true,
  state: {
    token: null,
    norms: {
      init: false,
      scores: {
        ACCOUNTABILITY: null,
        ADAPTABILITY: null,
        DECISION_MAKING: null,
        FLEXIBILITY: null,
        INTEGRITY: null,
        LEARNING_AGILITY: null,
        OWIWI: null, // total
        RESILIENCE: null,
        TEAMWORK: null
      },
      time: {
        ACCOUNTABILITY: null,
        ADAPTABILITY: null,
        DECISION_MAKING: null,
        FLEXIBILITY: null,
        INTEGRITY: null,
        LEARNING_AGILITY: null,
        OWIWI: null, // total
        RESILIENCE: null,
        TEAMWORK: null
      }
    },
    notification: {
      type: 'info',
      effect: 'dark',
      closable: true,
      title: '',
      description: '',
      show: false
    },
    languageCodes: {
      init: false,
      data: [
        {
          code: 'EN',
          name: 'English'
        }
      ]
    },
    translations: {
      BR: null,
      EN: null,
      GR: null,
      IT: null,
      ES: null,
      TR: null,
      KO: null,
      DE: null,
      SL: null
    },
    learningCenter: null
  },
  actions: {
    login: debounce(({ commit, getters }) => {
      const token = getters.CMSToken
      if (token) return token

      return axios.post(`${CMSUrl}auth/local`, {
        identifier: 'reader@owiwi.co.uk',
        password: 'reader@owiwi'
      }, {
        headers: {
          Authorization: '' // temp disable the bearer token set for owiwi
        }
      })
        .then(res => {
          commit('storeToken', res.data.jwt)
        })
    }, 0),

    owiwiNorm: debounce(({ commit, getters }) => {
      if (!getters.CMSToken) return
      if (getters.norms.OWIWI) return

      return axios.get(`${CMSUrl}norms/owiwi`, {
        headers: {
          Authorization: 'Bearer ' + getters.CMSToken
        }
      })
        .then(res => {
          commit('owiwiNorm', res.data)
        })
    }, 0),

    norms: debounce(({ commit, getters }) => {
      if (!getters.CMSToken) return
      if (getters.normsInit) return

      return axios.get(`${CMSUrl}norms`, {
        headers: {
          Authorization: 'Bearer ' + getters.CMSToken
        }
      })
        .then(res => {
          commit('norms', res.data)
        })
    }, 0),

    notification: debounce(({ commit, getters, rootGetters }) => {
      if (!getters.CMSToken) return
      if (!rootGetters.isAuthenticated) {
        return Promise.resolve()
      }

      const cacheKey = 'dashboardNotification'

      if (rootGetters['cache/isValid'](cacheKey)) {
        return Promise.resolve()
      }

      const type = rootGetters.isOrganization ? 'recruiter' : 'candidate'

      return axios.get(`${CMSUrl + type}-notification`, {
        headers: {
          Authorization: 'Bearer ' + getters.CMSToken
        }
      })
        .then(res => {
          commit('cache/set', { key: cacheKey }, { root: true })

          commit('notification', res.data)
        })
    }, 0),

    getLanguageCodes: debounce(async({ commit, getters }) => {
      if (!getters.CMSToken) return
      if (getters.languageCodes.init) return

      return axios.get(`${CMSUrl}lang-codes`, {
        headers: {
          Authorization: 'Bearer ' + getters.CMSToken
        }
      })
        .then(res => {
          commit('languageCodes', res.data)
        })
    }, 0),

    getLanguageCodesFromApi: async({ commit, getters }) => {
      if (getters.languageCodes.init) return

      let data = axios.get(`translations/codes`)
        .then(res => {
          commit('languageCodes', res.data)
          return res.data
        })

      return data
    },

    getLanguage: async({ commit, getters }, lang) => {
      if (!getters.CMSToken) return

      let data = await axios.get(`${CMSUrl}languages?_where[0][code]=${lang}`, {
        headers: {
          Authorization: 'Bearer ' + getters.CMSToken
        }
      })
        .then(res => {
          return res.data[0].i18n
        })

      return data
    },

    getLanguageFromApi: async({ commit, getters }, lang) => {
      let data = await axios.get(`translations/${lang}`)
        .then(res => {
          return res.data[0].i18n
        })

      return data
    },

    getLanguageDetailedFromApi: async({ commit, getters }, lang) => {
      let data = await axios.get(`translations/detailed/${lang}`)
        .then(res => {
          return res.data
        })

      return data
    },

    getLanguageFromBack: async({ commit, getters }, lang) => {
      let data = await axios.get(`translation/${lang}`)
        .then(res => {
          return res.data
        })

      return data
    },

    saveTranslation: async({ commit, getters }, data) => {
      return axios.post('translation', data)
        .then(res => {
          return res.status
        }).catch(axios.handleError)
    },

    LearningCenterContent: debounce(({ commit, getters }) => {
      if (!getters.CMSToken) return
      if (getters.learningCenter !== null) return getters.learningCenter

      return axios.get(`${CMSUrl}learning-center`, {
        headers: {
          Authorization: 'Bearer ' + getters.CMSToken
        }
      })
        .then(res => {
          commit('learningCenter', res.data)
        })
    }, 0)
  },

  getters: {
    CMSToken: state => {
      return state.token
    },

    notification: state => {
      return state.notification
    },

    normsInit: state => {
      return state.norms.init
    },

    norms: state => {
      return state.norms.scores
    },

    averageSkillTime: state => {
      return state.norms.time
    },

    translations: state => {
      return state.translations
    },

    language: state => lang => {
      return state.translations[lang]
    },

    languageCodes: state => {
      return state.languageCodes
    },

    languageCodesData: state => {
      return state.languageCodes.data
    },

    learningCenter: state => {
      return state.learningCenter
    }
  },

  mutations: {
    storeToken: (state, payload) => {
      state.token = payload
    },

    owiwiNorm: (state, payload) => {
      state.norms.scores.OWIWI = payload.scores.OWIWI
      state.norms.time.OWIWI = payload.time.OWIWI
    },

    notification: (state, payload) => {
      state.notification = payload
    },

    norms: (state, payload) => {
      state.norms = payload
      state.norms.init = true
    },

    translations: (state, payload) => {
      state.translations[payload.lang] = payload.messages
    },

    languageCodes: (state, payload) => {
      state.languageCodes.init = true
      state.languageCodes.data = payload
    },

    learningCenter: (state, payload) => {
      state.learningCenter = payload
    }
  }
}
